// External imports
import React, { useEffect, useState } from "react";
import { HiOutlineArrowRight } from "react-icons/hi2";

// Internal imports
import WebsiteButton from "../../components/core/button/WebsiteButton";
import MainPageLayout from "../../components/core/header/MainPageLayout";
//@ts-ignore
import outside from "./outside.jpg";
import {
  PageContainer,
  TextSection,
  Title,
  Subtitle,
  Description,
  ButtonContainer,
  ImageSection,
  Image,
} from "./FrontPage.styles";
import {
  getJwt,
  SetFromApiDataType,
} from "../../core/LocalStorage/AuthSession/core";

// Dependency injection
import { GetRoleType } from "../../core/LocalStorage/AuthSession/core";
import { SsoLoginType } from "../../core/ServerApiLayer/VOne/auth/ssoLogin";

// Language Context
import { useLanguage } from "../../core/translations/LanguageContext";

// Translations
import { FrontPageTranslations } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { SupportedLanguage } from "../../core/translations/supportedLanguages";

// Agreement modal
import AgreementModal from "../../components/core/modal/agreement-modal/AgreementModal";
import { AgreementResponse } from "../../core/ServerApiLayer/VOne/auth/getAgreements";

/**
 * Props for the FrontPage component
 */
export interface FrontPageProps {
  getRoleApiCall: GetRoleType;
  ssoLoginApiCall: SsoLoginType;
  setSessionData: SetFromApiDataType;
  getAgreements: any;
  dataAgree: any;
}

const TRANSLATIONS: Record<SupportedLanguage, FrontPageTranslations> = {
  en: enTranslations,
  de: deTranslations,
};

export const FrontPage: React.FC<FrontPageProps> = ({
                                                      getRoleApiCall,
                                                      ssoLoginApiCall,
                                                      setSessionData,
                                                      getAgreements,
                                                      dataAgree,
                                                    }) => {
  const { language } = useLanguage();
  const translations = TRANSLATIONS[language];
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [pendingJwt, setPendingJwt] = useState<string | null>(null);

  useEffect(() => {
    if (window.location.hash.startsWith("#id_token=") && !getJwt()) {
      const hash = window.location.hash.substring(1); // remove #
      const params = new URLSearchParams(hash);
      const idToken = params.get("id_token");

      if (idToken) {
        ssoLoginApiCall(idToken).then(async (response) => {
          if (response.status === 200 && response.data) {
            const loginResponse = response.data;
            setSessionData(
                loginResponse.user_session,
                loginResponse.jwt,
                loginResponse.refresh_token
            );

            const jwt = loginResponse.jwt;
            const agreementsResponse = await getAgreements(jwt);

            const needsAgreement =
                agreementsResponse.status === 200 &&
                agreementsResponse.data &&
                !agreementsResponse.data.some(
                    (a: AgreementResponse[0]) =>
                        a.agreement_type_name === "general data agreement" &&
                        a.agreement_type_version >= 1
                );

            if (needsAgreement) {
              setPendingJwt(jwt);
              setShowAgreementModal(true);
            } else {
              window.history.replaceState({}, document.title, "/");
              window.location.reload();
            }
          } else {
            console.error("SSO login failed:", response.error);
          }
        });
      }
    }
  }, []);

  const onSubmitAgree = async () => {
    if (!pendingJwt) return;

    const res = await dataAgree(pendingJwt);
    if (res.status === 200) {
      setShowAgreementModal(false);
      window.history.replaceState({}, document.title, "/");
      window.location.reload();
    } else {
      console.error("Agreement signing failed");
    }
  };

  const loggedIn = getJwt();
  const role = getRoleApiCall();

  return (
      <MainPageLayout>
        {showAgreementModal && <AgreementModal onSubmitAgree={onSubmitAgree} />}
        <PageContainer>
          <TextSection>
            <Title>
              <span>{translations.simulationTraining}</span>{" "}
              <Subtitle>{translations.medicalProfessionals}</Subtitle>
            </Title>
            <Description>{translations.description}</Description>
            <ButtonContainer>
              <div>
                {loggedIn ? (
                    role === "TEACHER" ? (
                        <WebsiteButton href="/home" tag="a">
                          {translations.buttons.manageClasses}
                        </WebsiteButton>
                    ) : (
                        <WebsiteButton href="/start" tag="a">
                          {translations.buttons.joinClass}
                        </WebsiteButton>
                    )
                ) : (
                    <WebsiteButton href="/login" tag="a">
                      {translations.buttons.signIn}
                    </WebsiteButton>
                )}
              </div>

              <WebsiteButton
                  tag="a"
                  iconPosition="right"
                  icon={<HiOutlineArrowRight size="1.3em" />}
                  outline
                  href="/demo"
              >
                {translations.buttons.bookDemo}
              </WebsiteButton>
            </ButtonContainer>
          </TextSection>
          <ImageSection>
            <Image src={outside} alt={translations.imageAlt} />
          </ImageSection>
        </PageContainer>
      </MainPageLayout>
  );
};
